import { useState } from 'react';
import { Panorama360 } from './Panorama360';

/**
 * Two equirectangular 360 viewers (Original | Enhanced) sharing one
 * ``{yaw, pitch, fov}`` state, so dragging either viewer pans both in
 * lockstep. Responsive: stacked on narrow screens, side-by-side on
 * ``md`` and up.
 */
export interface SyncedPanoramaPairProps {
  originalUrl: string;
  enhancedUrl: string;
  className?: string;
}

const DEFAULT_FOV = 75;

export function SyncedPanoramaPair({
  originalUrl,
  enhancedUrl,
  className,
}: SyncedPanoramaPairProps) {
  const [view, setView] = useState({ yaw: 0, pitch: 0, fov: DEFAULT_FOV });

  const handleChange = (next: { yaw: number; pitch: number }) => {
    setView((v) => ({ ...v, yaw: next.yaw, pitch: next.pitch }));
  };

  const handleFovChange = (fov: number) => {
    setView((v) => ({ ...v, fov }));
  };

  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${className ?? ''}`}>
      <figure className="m-0">
        <div className="relative aspect-[16/9] w-full overflow-hidden rounded-lg border border-surface-200 bg-surface-900">
          <Panorama360
            url={originalUrl}
            yaw={view.yaw}
            pitch={view.pitch}
            fov={view.fov}
            onChange={handleChange}
            onFovChange={handleFovChange}
            className="h-full w-full"
          />
          <span className="absolute left-2 top-2 rounded-md bg-black/55 px-2 py-0.5 text-xs font-medium text-white backdrop-blur">
            Original
          </span>
        </div>
      </figure>
      <figure className="m-0">
        <div className="relative aspect-[16/9] w-full overflow-hidden rounded-lg border border-surface-200 bg-surface-900">
          <Panorama360
            url={enhancedUrl}
            yaw={view.yaw}
            pitch={view.pitch}
            fov={view.fov}
            onChange={handleChange}
            onFovChange={handleFovChange}
            className="h-full w-full"
          />
          <span className="absolute left-2 top-2 rounded-md bg-indigo-500/70 px-2 py-0.5 text-xs font-medium text-white backdrop-blur">
            Enhanced
          </span>
        </div>
      </figure>
    </div>
  );
}
